import React, { useState } from "react";
import { FaCog, FaInfoCircle, FaRobot, FaPencilAlt, FaEye } from "react-icons/fa";
import MonoButton from "../kits/DesignKit/MonoButton";
import ModalWrapper from "./utils/ModalWrapper";
import PaperBG from "../kits/DesignKit/PaperBG";

/**
 * ZenEditor Settings Component
 * Manages editor preferences and AI configuration
 */
function ZenEditorSettings({ onClose }) {
  // Load settings from localStorage
  const loadSettings = () => {
    const saved = localStorage.getItem("zeneditor_settings");
    if (saved) {
      return JSON.parse(saved);
    }
    return {
      // AI Settings
      aiModel: "llama3.1",
      aiTemperature: 0.7,
      aiMaxTokens: 500,
      aiApiUrl: process.env.REACT_APP_OLLAMA_API_URL || "http://localhost:11434",

      // Editor Settings
      fontSize: 12,
      theme: "light",
      autoSave: true,
      autoSaveInterval: 30, // seconds

      // Preview Settings
      livePreview: true,
      previewPosition: "right",
      syncScrolling: false,

      // Advanced
      showLineNumbers: false,
      wordWrap: true,
      spellCheck: true,
    };
  };

  const [settings, setSettings] = useState(loadSettings());
  const [activeTab, setActiveTab] = useState("ai"); // "ai", "editor", "preview", "about"
  const [hasChanges, setHasChanges] = useState(false);

  // Save settings to localStorage
  const saveSettings = () => {
    localStorage.setItem("zeneditor_settings", JSON.stringify(settings));
    setHasChanges(false);

    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('zeneditor:settings-updated'));

    alert("✅ Einstellungen gespeichert!");
  };

  // Update a setting
  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Reset to defaults
  const resetSettings = () => {
    if (window.confirm("Alle Einstellungen auf Standard zurücksetzen?")) {
      localStorage.removeItem("zeneditor_settings");
      setSettings(loadSettings());
      setHasChanges(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose}>
      <PaperBG className="max-w-full mx-auto rounded-xl p-8">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FaCog className="text-brand" />
            Einstellungen
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            ZenEditor Konfiguration
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-300 mb-4">
          <div className="flex gap-2 flex-wrap">
            {[
              { id: "ai", label: "AI", Icon: FaRobot },
              { id: "editor", label: "Editor", Icon: FaPencilAlt },
              { id: "preview", label: "Preview", Icon: FaEye },
              { id: "about", label: "Über", Icon: FaInfoCircle },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-3 py-2 text-xs font-mono border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === id
                    ? "border-brand text-brand font-semibold"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon className="text-xs" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="mb-4">
          {/* AI Settings */}
          {activeTab === "ai" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">AI-Modell</label>
                <select
                  value={settings.aiModel}
                  onChange={(e) => updateSetting("aiModel", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="llama3.1">✓ Llama 3.1 (8B) - Empfohlen</option>
                  <option value="llama3.2">✓ Llama 3.2 (3B) - Schnell</option>
                  <option value="mistral">✓ Mistral 7B - Deutsch-optimiert</option>
                  <option value="codellama">✓ CodeLlama - Code-Spezialist</option>
                  <option value="gpt-oss:20b">✓ GPT-OSS (20B) - Beste Qualität</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">
                  Wähle das AI-Modell für Textgenerierung
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Temperatur: {settings.aiTemperature}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.aiTemperature}
                  onChange={(e) => updateSetting("aiTemperature", parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>Präzise (0.0)</span>
                  <span>Kreativ (1.0)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Max. Tokens: {settings.aiMaxTokens}
                </label>
                <input
                  type="range"
                  min="100"
                  max="2000"
                  step="100"
                  value={settings.aiMaxTokens}
                  onChange={(e) => updateSetting("aiMaxTokens", parseInt(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Maximale Länge der AI-Antworten
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">API-URL</label>
                <input
                  type="text"
                  value={settings.aiApiUrl}
                  onChange={(e) => updateSetting("aiApiUrl", e.target.value)}
                  placeholder="http://localhost:11434"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Ollama API-Endpunkt (lokal oder remote)
                </p>
              </div>
            </div>
          )}

          {/* Editor Settings */}
          {activeTab === "editor" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Schriftgröße: {settings.fontSize}px
                </label>
                <input
                  type="range"
                  min="10"
                  max="20"
                  step="1"
                  value={settings.fontSize}
                  onChange={(e) => updateSetting("fontSize", parseInt(e.target.value))}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Theme</label>
                <select
                  value={settings.theme}
                  onChange={(e) => updateSetting("theme", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="light">Hell</option>
                  <option value="dark">Dunkel</option>
                  <option value="auto">Automatisch</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.autoSave}
                    onChange={(e) => updateSetting("autoSave", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Automatisch speichern</span>
                </label>

                {settings.autoSave && (
                  <div className="ml-7">
                    <label className="block text-sm mb-2">
                      Intervall: {settings.autoSaveInterval} Sekunden
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="120"
                      step="10"
                      value={settings.autoSaveInterval}
                      onChange={(e) => updateSetting("autoSaveInterval", parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                )}

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.wordWrap}
                    onChange={(e) => updateSetting("wordWrap", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Automatischer Zeilenumbruch</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.spellCheck}
                    onChange={(e) => updateSetting("spellCheck", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Rechtschreibprüfung</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.showLineNumbers}
                    onChange={(e) => updateSetting("showLineNumbers", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Zeilennummern anzeigen</span>
                </label>
              </div>
            </div>
          )}

          {/* Preview Settings */}
          {activeTab === "preview" && (
            <div className="space-y-6">
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.livePreview}
                    onChange={(e) => updateSetting("livePreview", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Live-Preview aktivieren</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.syncScrolling}
                    onChange={(e) => updateSetting("syncScrolling", e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Synchrones Scrollen</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Preview-Position</label>
                <select
                  value={settings.previewPosition}
                  onChange={(e) => updateSetting("previewPosition", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="right">Rechts</option>
                  <option value="bottom">Unten</option>
                  <option value="tab">Als Tab</option>
                </select>
              </div>
            </div>
          )}

          {/* About */}
          {activeTab === "about" && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <FaInfoCircle className="text-brand" />
                  ZenEditor Demo
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                  Hybrid Markdown/EditorJS Editor mit AI-Features
                </p>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>Version:</strong> 1.0.0-beta</p>
                  <p><strong>AI-Engine:</strong> Ollama (Open Source)</p>
                  <p><strong>Lizenz:</strong> MIT</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-sm">Features</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>✅ Markdown & EditorJS Support</li>
                  <li>✅ AI-gestützte Textbearbeitung</li>
                  <li>✅ Live-Preview</li>
                  <li>✅ Export als .md Datei</li>
                  <li>✅ LocalStorage Persistenz</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold mb-2 text-sm">Tastatur-Shortcuts</h4>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><kbd className="bg-gray-200 px-1 rounded">Cmd/Ctrl + S</kbd> - Speichern</p>
                  <p><kbd className="bg-gray-200 px-1 rounded">Cmd/Ctrl + B</kbd> - Fett</p>
                  <p><kbd className="bg-gray-200 px-1 rounded">Cmd/Ctrl + I</kbd> - Kursiv</p>
                  <p><kbd className="bg-gray-200 px-1 rounded">Cmd/Ctrl + K</kbd> - Link</p>
                </div>
              </div>

              <div className="text-center pt-4">
                <p className="text-xs text-gray-600">
                  Erstellt mit ❤️ von{" "}
                  <a href="https://denisbitter.de" className="text-brand underline">
                    Denis Bitter
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-300 pt-4 mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-gray-600 text-center sm:text-left">
            {hasChanges && (
              <span className="text-orange-600 font-semibold">
                ⚠️ Nicht gespeicherte Änderungen
              </span>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={resetSettings}
              className="flex-1 sm:flex-none px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            >
              Zurücksetzen
            </button>
            <MonoButton
              onClick={saveSettings}
              disabled={!hasChanges}
              className="flex-1 sm:flex-none text-xs px-3 py-2 flex items-center justify-center gap-2"
            >
            
              Speichern
            </MonoButton>
          </div>
        </div>
      </PaperBG>
    </ModalWrapper>
  );
}

export default ZenEditorSettings;
